import { computed, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getArticleDetails } from './api.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import { devLog } from '@/common/debug';

export interface ArticleData {
  title: string;
  body: string;
}

/**
 * Fetch article details from the API
 * Returns transformed article data with title and body
 */
const fetchArticleDetails = async (articleId: string | number): Promise<ArticleData> => {
  const response = await getArticleDetails.cached({ articleId });
  return {
    title: response.data.title,
    body: response.data.body,
  };
};

/**
 * TanStack Query hook to fetch article details
 * Provides automatic caching, loading states, and error handling
 *
 * @param articleId - Computed ref containing the article ID
 * @returns TanStack Query result with article data, loading state, and error
 *
 * @example
 * ```ts
 * const articleId = computed(() => route?.query?.articleId as string);
 * const { data, isLoading, error } = useArticleDetails(articleId);
 * ```
 */
export const useArticleDetails = (articleId: ComputedRef<string | number | undefined>) => {
  const mockedFlowStore = useMockedFlowStore();
  const { mockedArticle, hasMockedArticle } = storeToRefs(mockedFlowStore);

  return useQuery({
    queryKey: ['article', articleId],
    queryFn: async (): Promise<ArticleData> => {
      if (!articleId.value) {
        throw new Error('Article ID is required');
      }

      // Return mocked article data if available in mocked flow
      if (hasMockedArticle.value && mockedArticle.value) {
        devLog.debug('[ARTICLE_API] Using mocked article data', mockedArticle.value);
        return {
          title: mockedArticle.value.title,
          body: mockedArticle.value.body,
        };
      }

      // Otherwise fetch from API
      return fetchArticleDetails(articleId.value);
    },
    enabled: computed(() => !!articleId.value),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    retry: true,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 60_000),
  });
};
